// PalmsScene — the original Coravu palm-fronds scene.
//
// Visual: tall radiating palm fronds anchored off-screen in two
// corners. Top-of-section palm fans outward from the top edge;
// bottom-of-section palm tucks down into the seam. Used on the
// student welcome page where the visual feel is "established
// home base, returning user."
//
// Seam strategy: the beige-tone bottom palm is anchored so its
// trunk exits the section bottom near `SEAM_ANCHOR_X`; the
// green-tone top palm anchors so its trunk enters the section
// top near the SAME X. The two trunks read as one tree growing
// through the cream/forest boundary.
//
// To keep the scene visually consistent if positioning is ever
// tuned: edit SEAM_ANCHOR_X / the bottom-palm `left` / the
// top-palm `left` together — they're three knobs on one
// alignment.

import { Platform, View } from "react-native";
import { COLORS } from "../../../lib/brand";
import Svg, { G, Path } from "react-native-svg";

import type { SceneTone } from "./types";

const CREAM_STROKE = COLORS.LINEN.hex;
const SAGE_STROKE = COLORS.SAGE.hex;

// Section-coordinate X where the seam-crossing trunks anchor. Edit
// in tandem with the bottom-palm `left` (beige) and top-palm
// `left` (green) values below if you want the trunks to land at
// a different X.
//
// Currently informational — the trunk positions below are tuned
// to produce a near-aligned result at SEAM_ANCHOR_X ≈ 240.
// const SEAM_ANCHOR_X = 240;

export function PalmsScene({ tone }: { tone: SceneTone }) {
  if (Platform.OS !== "web") return null;

  const isBeige = tone === "on-beige";
  const stroke = isBeige ? SAGE_STROKE : CREAM_STROKE;

  // Sage on cream needs more weight than cream on forest to read
  // — the cream-on-forest tone goes way down so it whispers
  // instead of overpowering the dark band.
  const primaryOpacity = isBeige ? 0.5 : 0.14;
  const accentOpacity = isBeige ? 0.35 : 0.1;

  return (
    <View
      pointerEvents="none"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
      }}
    >
      {/* TOP palm — anchored top-RIGHT for on-beige, top-LEFT
          (horizontally mirrored) for on-green. The on-green tone
          is pulled UP past the section top (top: -180) and shifted
          hard left (left: -380) so the trunk's section X coordinate
          matches the beige bottom palm's trunk X at the seam. */}
      <Svg
        width={1100}
        height={1100}
        viewBox="0 0 1100 1100"
        style={{
          position: "absolute",
          ...(isBeige
            ? { right: -80, top: -60 }
            : ({
                left: -380,
                top: -180,
                transform: [{ scaleX: -1 }] as unknown as never,
              } as object)),
          opacity: primaryOpacity,
        }}
      >
        <G stroke={stroke} strokeLinecap="round" fill="none">
          <Path
            d="M550 1060 C535 880 500 700 460 560 C428 440 385 340 355 240 C330 160 318 90 310 40"
            strokeWidth={4}
          />
          <Path
            d="M460 560 C560 510 680 478 790 440 C880 408 960 368 1020 328 C1070 295 1095 265 1100 240"
            strokeWidth={3}
          />
          <Path d="M475 620 C578 574 700 544 812 508 C904 478 988 438 1048 400" strokeWidth={2.5} />
          <Path
            d="M455 500 C550 452 666 420 772 384 C858 354 935 314 990 275 C1035 243 1058 214 1062 190"
            strokeWidth={2.2}
          />
          <Path d="M440 440 C530 392 640 360 742 325 C825 295 898 257 950 220" strokeWidth={2} />
          <Path d="M460 560 C365 510 248 478 142 440 C55 408 -22 368 -80 328" strokeWidth={3} />
          <Path d="M470 600 C372 554 252 524 142 488 C52 458 -30 418 -88 380" strokeWidth={2.5} />
          <Path d="M680 478 C672 448 658 422 638 394" strokeWidth={1.4} />
          <Path d="M760 448 C752 418 738 392 718 364" strokeWidth={1.4} />
          <Path d="M840 414 C832 384 818 358 798 330" strokeWidth={1.4} />
          <Path d="M600 500 C592 470 578 444 560 416" strokeWidth={1.4} />
          <Path d="M240 478 C248 448 262 422 282 394" strokeWidth={1.4} />
          <Path d="M160 448 C168 418 182 392 202 364" strokeWidth={1.4} />
          <Path d="M390 320 C440 292 510 272 575 248 C630 228 678 204 710 180" strokeWidth={2} />
          <Path d="M375 260 C422 232 488 212 550 190 C602 172 648 150 678 128" strokeWidth={1.8} />
          <Path d="M362 200 C405 174 468 156 526 136 C576 118 618 97 645 76" strokeWidth={1.6} />
        </G>
      </Svg>

      {/* BOTTOM palm — anchored bottom-LEFT for on-beige (offset
          past the section bottom so its fronds extend down into
          the seam), bottom-RIGHT for on-green (mirror image,
          fully inside the section for balance since there's no
          section below the green to connect to). */}
      <Svg
        width={820}
        height={820}
        viewBox="0 0 820 820"
        style={{
          position: "absolute",
          ...(isBeige
            ? ({
                left: -120,
                bottom: -180,
                transform: [{ rotate: "160deg" }, { scaleX: -1 }] as unknown as never,
              } as object)
            : ({
                right: -120,
                bottom: -100,
                transform: [{ rotate: "160deg" }] as unknown as never,
              } as object)),
          opacity: accentOpacity,
        }}
      >
        <G stroke={stroke} strokeLinecap="round" fill="none">
          <Path
            d="M410 790 C400 660 372 530 340 420 C312 320 280 240 250 160 C228 100 218 50 212 10"
            strokeWidth={3.5}
          />
          <Path d="M340 420 C420 380 510 354 600 322 C672 296 738 264 790 230" strokeWidth={2.5} />
          <Path d="M350 470 C435 432 535 408 622 378 C698 352 768 320 815 290" strokeWidth={2.2} />
          <Path d="M338 380 C262 340 168 312 80 280 C18 256 -38 226 -70 200" strokeWidth={2.5} />
          <Path d="M345 440 C265 400 168 372 78 340 C12 316 -48 286 -82 256" strokeWidth={2.2} />
          <Path d="M280 240 C322 218 380 200 432 180 C475 162 510 142 532 122" strokeWidth={1.8} />
          <Path d="M268 180 C306 158 360 142 408 124 C448 108 480 88 500 68" strokeWidth={1.6} />
        </G>
      </Svg>
    </View>
  );
}
